import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const IssueShareCertificateTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.ShareCertificate
  );

  const handleViewFile = () => viewUploadedFile(file?.id);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads Signed Share Certificate" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Signed Share Certificate uploaded by client"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div>
                <Button
                  size="sm"
                  onClick={uploadModalVisibility.show}
                  variant="primary"
                >
                  <Icons.Upload01 className="size-4" />
                  Upload
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Signed Share Certificate" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Signed Share Certificate" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Signed Share Certificate uploaded"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <CorporationUploadFileModal
        serviceRequestTaskId={task.id}
        file={file}
        setFile={setFile}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        onUploadSuccess={() =>
          updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          })
        }
        documentType={LegacyDocumentType.ShareCertificate}
        heading="Upload Share Certificate"
        serviceRequest={serviceRequest}
      />
    </div>
  );
};
